/**
 * Section Mappings Utility
 *
 * Provides visual indicators (icons, colors) and metadata for translation sections.
 * This centralized configuration ensures consistency across the admin interface
 * and makes it easy to update section appearance in one place.
 *
 * Design Rationale:
 * - Icons provide instant visual recognition (humans process visuals 60,000x faster than text)
 * - Colors create visual memory and help differentiate sections quickly
 * - Descriptions provide context for admins who are editing translations
 * - Centralized config follows DRY principle and single source of truth
 */

export interface SectionConfig {
  /** Icon name from @lnr-v2/ui Icon component (Lucide icons) */
  icon: string;
  /** Hex color for section badge background */
  color: string;
  /** Human-readable section name for display */
  label: string;
  /** Contextual description explaining where this section appears */
  description: string;
}

/**
 * Section mappings for all known website sections.
 * Each section has a unique visual identity (icon + color) for quick recognition.
 *
 * Color Strategy:
 * - Indigo (#6366f1) - Hero, Filters - Important, attention-grabbing content
 * - Purple (#8b5cf6) - Agency, Navigation - Navigational elements
 * - Pink (#ec4899) - Manifesto - Emotional, value-driven content
 * - Amber (#f59e0b) - Cases - Showcasing work
 * - Emerald (#10b981) - Services - Growth, offerings
 * - Teal (#14b8a6) - Categories - Organization
 * - Cyan (#06b6d4) - Clients - Trust, partnership
 * - Blue (#3b82f6) - Headers - Clear, professional
 * - Red (#ef4444) - Contact - Action, urgency
 * - Slate (#64748b) - Footer, Common - Neutral, supporting
 * - Violet (#a855f7) - Buttons - Interactive elements
 */
export const sectionMappings: Record<string, SectionConfig> = {
  hero: {
    icon: 'home',
    color: '#6366f1',
    label: 'Hero Section',
    description: 'Main banner at the top of the page with headline content',
  },
  showreel: {
    icon: 'play-circle',
    color: '#7c3aed',
    label: 'Showreel',
    description: 'Video showreel section with play button',
  },
  agency: {
    icon: 'briefcase',
    color: '#8b5cf6',
    label: 'Agency Section',
    description: 'Information about the agency, team, and capabilities',
  },
  manifesto: {
    icon: 'book-open',
    color: '#ec4899',
    label: 'Manifesto',
    description: 'Company philosophy and values',
  },
  cases: {
    icon: 'folder',
    color: '#f59e0b',
    label: 'Cases Section',
    description: 'Portfolio showcase of projects and work',
  },
  services: {
    icon: 'grid',
    color: '#10b981',
    label: 'Services',
    description: 'Services offered by the agency',
  },
  categories: {
    icon: 'tag',
    color: '#14b8a6',
    label: 'Categories',
    description: 'Service categories and classifications',
  },
  clients: {
    icon: 'users',
    color: '#06b6d4',
    label: 'Clients Section',
    description: 'Client logos and testimonials',
  },
  metrics: {
    icon: 'bar-chart-2',
    color: '#0ea5e9',
    label: 'Metrics Section',
    description: 'Key numbers and statistics display',
  },
  header: {
    icon: 'layout',
    color: '#3b82f6',
    label: 'Page Header',
    description: 'Main heading and introductory content',
  },
  filters: {
    icon: 'filter',
    color: '#6366f1',
    label: 'Filters',
    description: 'Filtering and sorting controls',
  },
  contact: {
    icon: 'mail',
    color: '#ef4444',
    label: 'Contact Section',
    description: 'Contact information and call-to-action',
  },
  navigation: {
    icon: 'menu',
    color: '#8b5cf6',
    label: 'Navigation',
    description: 'Site navigation menu items',
  },
  buttons: {
    icon: 'mouse-pointer',
    color: '#a855f7',
    label: 'Buttons',
    description: 'Interactive button elements',
  },
  footer: {
    icon: 'layout',
    color: '#64748b',
    label: 'Footer',
    description: 'Bottom of page content and links',
  },
};

/**
 * Default section config for unknown sections.
 * Uses neutral styling to avoid confusion.
 */
const defaultSectionConfig: SectionConfig = {
  icon: 'file-text',
  color: '#94a3b8',
  label: 'Content',
  description: 'General website content',
};

/**
 * Get section configuration by section name.
 * Returns default config if section is not found in mappings.
 *
 * @param section - Section identifier (e.g., 'hero', 'footer', 'navigation')
 * @returns Section configuration with icon, color, label, and description
 *
 * @example
 * const config = getSectionConfig('hero');
 * // Returns: { icon: 'home', color: '#6366f1', label: 'Hero Section', ... }
 */
export function getSectionConfig(section: string): SectionConfig {
  return sectionMappings[section] || defaultSectionConfig;
}

/**
 * Generate website URL for a translation based on page, section, and element.
 *
 * URL Strategy:
 * - Home page: baseUrl + '/?section=' + section + '#' + element
 * - Cases page: baseUrl + '/cases?section=' + section + '#' + element
 * - Common: baseUrl + '#' + element (appears on all pages, use home as default)
 *
 * The section query parameter helps with analytics and debugging,
 * while the anchor provides precise scrolling to the element.
 *
 * @param page - Page identifier ('home', 'cases', 'common')
 * @param section - Section identifier (e.g., 'hero', 'footer')
 * @param element - Element identifier (e.g., 'title', 'subtitle')
 * @param baseUrl - Base website URL (defaults to https://lnr.agency)
 * @returns Full URL to the translation location on the website
 *
 * @example
 * generateWebsiteUrl('home', 'hero', 'word_1');
 * // Returns: 'https://lnr.agency/?section=hero#word_1'
 *
 * generateWebsiteUrl('cases', 'header', 'title');
 * // Returns: 'https://lnr.agency/cases?section=header#title'
 *
 * generateWebsiteUrl('common', 'navigation', 'home');
 * // Returns: 'https://lnr.agency/#home'
 */
export function generateWebsiteUrl(
  page: string,
  section: string,
  element: string,
  baseUrl: string = 'https://lnr.agency'
): string {
  // Remove trailing slash from baseUrl if present
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');

  switch (page) {
    case 'home':
      return `${cleanBaseUrl}/?section=${section}#${element}`;

    case 'cases':
      return `${cleanBaseUrl}/cases?section=${section}#${element}`;

    case 'common':
      // Common elements appear on all pages, default to home with just anchor
      return `${cleanBaseUrl}/#${element}`;

    default:
      // For unknown pages, try to use the page name as path
      return `${cleanBaseUrl}/${page}?section=${section}#${element}`;
  }
}

/**
 * Get a lighter version of the section color for background use.
 * Adds alpha transparency to create a subtle background effect.
 *
 * @param color - Hex color code (e.g., '#6366f1')
 * @param alpha - Alpha transparency (0-1, defaults to 0.1)
 * @returns RGBA color string
 *
 * @example
 * getLighterColor('#6366f1', 0.1);
 * // Returns: 'rgba(99, 102, 241, 0.1)'
 */
export function getLighterColor(color: string, alpha: number = 0.1): string {
  // Convert hex to RGB
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
